// Resolve the admin page background color from the tenant theme settings,
// mirroring the storefront + ThemeManager logic exactly so the admin chrome
// matches the storefront:
//   - pageBackgroundMode 'custom' -> theme.pageBackgroundColor (default #f5f3ff)
//   - otherwise ('auto')          -> menuColor mixed 75% toward white
//
// This intentionally reads from the saved theme (GET /admin/theme) rather than
// hardcoding a value, so each tenant's admin matches their storefront.

const DEFAULT_MENU_COLOR = '#6236ff';
const DEFAULT_PAGE_BACKGROUND = '#f5f3ff';

export function normalizeHex(value) {
  if (typeof value !== 'string') return null;
  const hex = value.trim();
  if (!hex.startsWith('#')) return null;
  const raw = hex.slice(1);
  if (raw.length === 3) {
    return `#${raw
      .split('')
      .map((ch) => `${ch}${ch}`)
      .join('')}`;
  }
  if (raw.length === 6) return `#${raw}`;
  return null;
}

export function mixHex(hex, otherHex, amount) {
  const toRgb = (input) => {
    const normalized = normalizeHex(input);
    if (!normalized) return null;
    const intVal = parseInt(normalized.slice(1), 16);
    return {
      r: (intVal >> 16) & 255,
      g: (intVal >> 8) & 255,
      b: intVal & 255,
    };
  };

  const a = toRgb(hex);
  const b = toRgb(otherHex);
  if (!a || !b) return hex;
  const t = Math.min(1, Math.max(0, amount));
  const r = Math.round(a.r + (b.r - a.r) * t);
  const g = Math.round(a.g + (b.g - a.g) * t);
  const bb = Math.round(a.b + (b.b - a.b) * t);
  return `#${[r, g, bb].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Compute the page background color from a tenant theme object.
 * @param {object} theme - the theme as returned by GET /admin/theme
 * @returns {string} a CSS color value
 */
export function resolvePageBackground(theme) {
  const t = theme && typeof theme === 'object' ? theme : {};
  const mode = String(t.pageBackgroundMode || 'auto').toLowerCase();
  const menuBaseColor = normalizeHex(t.menuColor) || DEFAULT_MENU_COLOR;

  if (mode === 'custom') {
    return normalizeHex(t.pageBackgroundColor) || DEFAULT_PAGE_BACKGROUND;
  }
  // 'auto' (default): a light tint of the tenant menu color, same as storefront.
  return mixHex(menuBaseColor, '#ffffff', 0.75);
}

export { DEFAULT_PAGE_BACKGROUND };
